/**
 * Clicking a blade-length pill navigates straight to that variant.
 *
 * Pills are plain <button> elements, not <a> tags: product-card.liquid's
 * "CRITICAL ADA FIX" gives every nested anchor `pointer-events: none` to
 * keep a card's single overlay link from colliding with a second one, so a
 * real link here would be inert.
 *
 * A button doesn't dodge the problem entirely, though. The overlay link and
 * the card's content are siblings, and the content side sets its own
 * `z-index` (1, against the overlay's 5) to make it a stacking context of
 * its own. That traps every descendant below the overlay for hit-testing no
 * matter what z-index the descendant itself asks for — verified with
 * headless-browser click tests against the real card CSS, including a
 * z-index of 2147483647 directly on a pill, which still lost. The theme's
 * own color/size swatches have this exact same problem; it isn't new here.
 *
 * A mouse click's `event.target` is whatever the browser's hit-test found —
 * the overlay link, not the pill underneath it. `elementsFromPoint` at the
 * same coordinates sees the whole stack regardless, pill included, so that
 * is what actually locates the real target. Keyboard activation (Tab, then
 * Enter/Space) never goes through hit-testing at all — the browser dispatches
 * the click straight at the focused element — so `event.target` is already
 * correct there and is checked first.
 */

const PILL_SELECTOR = '.blade-lengths__pill[data-variant-url]';

function pillFromEvent(event) {
  if (!(event.target instanceof Element)) return null;
  const direct = event.target.closest(PILL_SELECTOR);
  if (direct) return direct;
  // A pill can only sit under a click that landed inside its card; anywhere
  // else, skip the hit-test (it forced a style and layout pass on every click
  // on the page, menu clicks included).
  if (!event.target.closest('product-card')) return null;

  if (typeof event.clientX !== 'number' || typeof document.elementsFromPoint !== 'function') return null;

  const stack = document.elementsFromPoint(event.clientX, event.clientY);
  return stack.find((el) => el.matches?.(PILL_SELECTOR)) ?? null;
}

// CSS :hover never fires on a pill because the overlay link wins every
// hit-test. JS tracks the cursor instead and applies the hover class manually.
// (The SKU tooltip reads each pill's data-variant-sku separately, in
// search-sku-tooltip.js, which owns that tooltip.)
let hoveredPill = null;

function clearHover() {
  if (hoveredPill) hoveredPill.classList.remove('blade-lengths__pill--js-hover');
  hoveredPill = null;
}

// Pills and the overlay link both live inside <product-card>, so the hit-test
// only runs over a card that has pills, and at most once per frame.
let pointerX = 0;
let pointerY = 0;
let hoverFrame = 0;

function updateHover() {
  hoverFrame = 0;
  const stack = document.elementsFromPoint(pointerX, pointerY);
  const pill = stack.find((el) => el.matches?.(PILL_SELECTOR)) ?? null;

  if (pill === hoveredPill) return;

  clearHover();
  hoveredPill = pill;
  if (pill) pill.classList.add('blade-lengths__pill--js-hover');
}

document.addEventListener(
  'mousemove',
  (event) => {
    if (typeof document.elementsFromPoint !== 'function') return;

    const card = event.target instanceof Element ? event.target.closest('product-card') : null;
    if (!card || !card.querySelector(PILL_SELECTOR)) {
      if (hoverFrame) hoverFrame = (cancelAnimationFrame(hoverFrame), 0);
      clearHover();
      return;
    }

    pointerX = event.clientX;
    pointerY = event.clientY;
    if (!hoverFrame) hoverFrame = requestAnimationFrame(updateHover);
  },
  { passive: true }
);

document.addEventListener('mouseleave', clearHover, true);

// Capture phase: this has to run and call stopPropagation before the click
// reaches the overlay link's own default (native navigation) and before it
// bubbles to product-card.js's card-wide click-to-navigate handler, or both
// would still fire using the overlay's href instead of the pill's.
document.addEventListener(
  'click',
  (event) => {
    const pill = pillFromEvent(event);
    if (!pill) return;

    const url = pill.getAttribute('data-variant-url');
    if (!url) return;

    event.preventDefault();
    event.stopPropagation();

    const openInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (openInNewTab) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  },
  true,
);
